/**
 * <buy-buttons-v2> custom element for the V2 PDP add-to-cart control.
 *
 * Owns the single [data-atc-button]/[data-atc-label] pair rendered by
 * snippets/buy-buttons-v2.liquid (the buy_buttons block has "limit": 1,
 * so there is only ever one instance per page).
 *
 * Label/availability sync — listens for the two document CustomEvents
 * produced elsewhere on the V2 PDP:
 *   - `variant:change`, detail: { variant } — dispatched by
 *     assets/component-configuration-picker-v2.js on every option-radio
 *     change. variant.price is already an integer number of cents
 *     (Shopify Liquid/JSON convention used throughout this project, see
 *     that file's doc comment and assets/component-modules-addons-v2.js).
 *   - `addons:change`, detail: { items, totalPrice } — dispatched by
 *     assets/component-modules-addons-v2.js. totalPrice is also integer
 *     cents.
 * On either event the label is recomputed as
 * "ADD TO CART — {{ formatted(variant price + addons total) }}" and the
 * button's disabled state is refreshed from variant.available.
 *
 * Money formatting — the total is a runtime sum (variant + addons) that
 * can't be pre-rendered like component-configuration-picker-v2.js's price
 * map, so the shop's actual money format string is prerendered into the
 * DOM (data-money-format="{{ shop.money_format | escape }}" on the
 * <buy-buttons-v2> element, see snippets/buy-buttons-v2.liquid) and
 * formatting resolves in this order:
 *   1. Shopify.formatMoney(cents, format) with the data-attribute format,
 *      when the storefront runtime provides it (this theme never defines
 *      it itself — sections/collection-grid.liquid:773 uses the exact
 *      same guarded pattern, so this mirrors existing theme convention);
 *   2. a tiny local money_format interpreter (formatWithMoneyFormat
 *      below) that substitutes the standard {{amount*}} placeholders in
 *      the same format string — this keeps the JS-updated label
 *      identical to the SSR `| money` output on any store, whatever its
 *      money_format;
 *   3. window.formatMoney (assets/money-format.js) as a last resort only
 *      if the format string is somehow missing from the DOM.
 *
 * Add-on submit path — see snippets/buy-buttons-v2.liquid's doc comment
 * for the full capture-phase submit interception rationale. Summary: a
 * capture-phase `submit` listener on this element runs before the
 * <product-form> element's own bubble-phase listener (attached directly
 * to the <form>, see assets/product-form.js). When
 * window.V2Addons.getItems() (assets/component-modules-addons-v2.js) is
 * non-empty, this handler takes over: preventDefault +
 * stopImmediatePropagation, then POSTs a single JSON `items` array (main
 * variant + every selected add-on, quantity 1 each) to
 * window.routes.cart_add_url, mirroring assets/product-form.js's own
 * fetchConfig('javascript') header usage and its
 * `this.cart.renderContents(response)` cart-drawer refresh call — see
 * that file's onSubmitHandler for the pattern this mirrors. When there
 * are no add-ons selected, this handler is a no-op and the submit event
 * continues to product-form's default single-item AJAX handling
 * untouched.
 */
(function () {
  // Idempotency guard, same pattern as component-configuration-picker-v2.js
  // and component-modules-addons-v2.js.
  if (window.__v2BuyButtonsBound) return;
  window.__v2BuyButtonsBound = true;

  if (customElements.get('buy-buttons-v2')) return;

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents == null) return '0';
    var number = (cents / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsPart = '';
    if (parts[1]) centsPart = decimal + parts[1];
    return dollars + centsPart;
  }

  // Minimal interpreter for Shopify money format strings — substitutes
  // the standard {{amount*}} placeholders so JS-computed totals render
  // exactly like Liquid's `| money`. HTML tags occasionally present in
  // custom money_format values are stripped since the result is written
  // via textContent.
  function formatWithMoneyFormat(cents, format) {
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    var value;
    var placeholder = 'amount';
    if (match) placeholder = match[1];
    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return format.replace(placeholderRegex, value).replace(/<[^>]*>/g, '').trim();
  }

  // Whole-dollar amounts drop ".00" (same as Liquid money_without_trailing_zeros).
  function formatMoneyCents(cents, format) {
    return formatMoneyRaw(cents, format).replace(/\.00(?!\d)/, '');
  }

  function formatMoneyRaw(cents, format) {
    if (format) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, format);
      }
      return formatWithMoneyFormat(cents, format);
    }
    if (typeof window.formatMoney === 'function') {
      return '$' + window.formatMoney(cents);
    }
    return '$' + (Math.round(cents) / 100).toFixed(0);
  }

  function getAddonItems() {
    if (window.V2Addons && typeof window.V2Addons.getItems === 'function') {
      return window.V2Addons.getItems();
    }
    return [];
  }

  // Reserved variant-upsell items (assets/component-variant-upsell-v2.js), folded
  // into the ATC total + bundled submit exactly like add-ons.
  function getUpsellItems() {
    if (window.V2Upsell && typeof window.V2Upsell.getItems === 'function') {
      return window.V2Upsell.getItems();
    }
    return [];
  }

  // Half / half firmness layer (assets/component-half-half-v2.js).
  function getHalfHalfItems() {
    if (window.V2HalfHalf && typeof window.V2HalfHalf.getItems === 'function') {
      return window.V2HalfHalf.getItems();
    }
    return [];
  }

  function getExtraItems() {
    return getAddonItems().concat(getUpsellItems(), getHalfHalfItems());
  }

  customElements.define(
    'buy-buttons-v2',
    class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[data-atc-button]');
        this.label = this.querySelector('[data-atc-label]');
        this.spinner = this.querySelector('.loading-overlay__spinner');
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.errorWrapper
          ? this.errorWrapper.querySelector('.product-form__error-message')
          : null;
        // Same precedence as assets/product-form.js's constructor.
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this._variantId = this.form ? this.form.querySelector('[name="id"]').value : null;
        this._variantPriceCents = parseInt(this.dataset.variantPriceCents, 10) || 0;
        this._variantAvailable = this.dataset.variantAvailable === 'true';
        this._addonsTotalCents = 0;
        this._upsellTotalCents = 0;
        this._halfHalfTotalCents = 0;

        this._onVariantChange = this._onVariantChange.bind(this);
        this._onAddonsChange = this._onAddonsChange.bind(this);
        this._onUpsellChange = this._onUpsellChange.bind(this);
        this._onHalfHalfChange = this._onHalfHalfChange.bind(this);
        this._onSubmitCapture = this._onSubmitCapture.bind(this);

        document.addEventListener('variant:change', this._onVariantChange);
        document.addEventListener('addons:change', this._onAddonsChange);
        document.addEventListener('upsell:change', this._onUpsellChange);
        document.addEventListener('halfhalf:change', this._onHalfHalfChange);
        this.addEventListener('submit', this._onSubmitCapture, true);

        this._updateLabel();
        this._updateDisabled();
      }

      disconnectedCallback() {
        document.removeEventListener('variant:change', this._onVariantChange);
        document.removeEventListener('addons:change', this._onAddonsChange);
        document.removeEventListener('upsell:change', this._onUpsellChange);
        document.removeEventListener('halfhalf:change', this._onHalfHalfChange);
      }

      _onVariantChange(event) {
        var variant = event.detail && event.detail.variant;
        if (!variant) return;
        this._variantId = variant.id;
        this._variantPriceCents = variant.price;
        this._variantAvailable = !!variant.available;
        this._updateLabel();
        this._updateDisabled();
      }

      _onAddonsChange(event) {
        var detail = event.detail || {};
        this._addonsTotalCents = detail.totalPrice || 0;
        this._updateLabel();
      }

      _onUpsellChange(event) {
        var detail = event.detail || {};
        this._upsellTotalCents = detail.totalPrice || 0;
        this._updateLabel();
      }

      _onHalfHalfChange(event) {
        var detail = event.detail || {};
        this._halfHalfTotalCents = detail.totalPrice || 0;
        this._updateLabel();
      }

      _updateLabel() {
        if (!this.label) return;
        if (!this._variantAvailable) {
          this.label.textContent = window.variantStrings && window.variantStrings.soldOut
            ? window.variantStrings.soldOut
            : 'Sold out';
          return;
        }
        var total =
          (this._variantPriceCents || 0) +
          (this._addonsTotalCents || 0) +
          (this._upsellTotalCents || 0) +
          (this._halfHalfTotalCents || 0);
        this.label.textContent = 'ADD TO CART — ' + formatMoneyCents(total, this.dataset.moneyFormat);
      }

      _updateDisabled() {
        if (!this.submitButton) return;
        if (this._variantAvailable) {
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute('aria-disabled');
        } else {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
        }
      }

      _onSubmitCapture(event) {
        var extraItems = getExtraItems();
        if (!extraItems.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this._submitWithAddons(extraItems);
      }

      _submitWithAddons(addonItems) {
        if (!this.form || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        var idInput = this.form.querySelector('[name="id"]');
        var variantId = idInput ? idInput.value : this._variantId;
        if (!variantId) return;

        var items = [{ id: parseInt(variantId, 10), quantity: 1 }];
        addonItems.forEach(function (item) {
          items.push({ id: parseInt(item.variantId, 10), quantity: item.qty || 1 });
        });

        var body = { items: items };
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map(function (section) {
            return section.id;
          });
          body.sections_url = window.location.pathname;
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        this._setLoading(true);
        this._setError(false);

        var config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        var self = this;
        fetch(window.routes.cart_add_url, config)
          .then(function (response) {
            return response.json();
          })
          .then(function (response) {
            if (response.status) {
              self._setError(response.description || response.message);
              return;
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'buy-buttons-v2', productVariantId: variantId });
            }

            // Reserved upsells are now in the cart — clear them so the cards
            // return to ADD and the ATC total drops back to the main variant.
            if (window.V2Upsell && typeof window.V2Upsell.reset === 'function') {
              window.V2Upsell.reset();
            }

            if (self.cart && typeof self.cart.renderContents === 'function') {
              self.cart.renderContents(response);
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch(function (e) {
            console.error(e);
            self._setError("Couldn't add to cart. Please try again.");
          })
          .finally(function () {
            self._setLoading(false);
          });
      }

      _setLoading(isLoading) {
        if (this.submitButton) {
          this.submitButton.classList.toggle('loading', isLoading);
          if (isLoading) {
            this.submitButton.setAttribute('aria-disabled', 'true');
          } else if (this._variantAvailable) {
            this.submitButton.removeAttribute('aria-disabled');
          }
        }
        if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
      }

      _setError(message) {
        if (!this.errorWrapper) return;
        this.errorWrapper.toggleAttribute('hidden', !message);
        if (message && this.errorMessage) this.errorMessage.textContent = message;
      }
    }
  );
})();
